import _ from "lodash";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import {
  IActiveFormValues,
  IFormsValue,
  ISubscriptionActiveForm,
  ISubscriptionForm,
  ISubscriptionItem,
  ISubscriptionItemCurrentUserSubscription,
  ISubscriptionReceiver,
  SubscriptionCollectedDataScope,
  SubscriptionPurchaseMethod,
  SubscriptionStatus,
} from "web/types/Subscription";

export const getInitialFormsValue = (
  item: ISubscriptionItemCurrentUserSubscription
) => {
  const init: IFormsValue = {};
  if (isArrayHasItems(item?.activeForms)) {
    item.activeForms.forEach(
      ({
        id: activeFormId,
        fieldsStructured,
        fields,
        receiver,
        receiverEmail,
      }) => {
        if (receiver === "others") {
          _.set(
            init,
            `[${item["@id"]}].activeForms[${activeFormId}].receiverEmail`,
            receiverEmail || ""
          );
        }
        fieldsStructured.forEach(({ id }) => {
          _.set(
            init,
            `[${item["@id"]}].activeForms[${activeFormId}].fields[${id}]`,
            fields?.[id]
          );
        });
      }
    );
  }
  if (isArrayHasItems(item?.forms)) {
    item.forms.forEach(({ id, fieldsStructured, fields }) => {
      fieldsStructured.forEach(({ type }) => {
        _.set(
          init,
          `[${item["@id"]}].forms[${id}].fields[${type}]`,
          fields[type]
        );
      });
    });
  }

  return init;
};

export const getNameOfBenefitWaitingToReplace = (
  benefitId: string,
  allBenefits: ISubscriptionItemCurrentUserSubscription[]
) =>
  allBenefits.find(
    (benefit) =>
      benefit.resignAfterActivation &&
      benefit.resignAfterActivation["@id"] === benefitId
  )?.name;

export const getNewActiveFormItem = (
  currentItem: ISubscriptionItem,
  formId: string,
  newValue: IActiveFormValues
) => {
  const newItem = _.cloneDeep(currentItem);
  const activeFormIndex = currentItem.activeForms.findIndex(
    ({ id }) => id === formId
  );
  return _.set(newItem, `activeForms.${activeFormIndex}.fields`, newValue);
};

export const COLLECTED_DATA_SCOPE = {
  NONE: SubscriptionCollectedDataScope.NULL,
  NAMES: SubscriptionCollectedDataScope.NAMES,
  ID: SubscriptionCollectedDataScope.PESEL,
  EMAIL: SubscriptionCollectedDataScope.EMAIL,
  ID_AND_EMAIL: SubscriptionCollectedDataScope.EMAIL_WITH_PESEL,
  ACTIVE_FORM: SubscriptionCollectedDataScope.ACTIVE_FORM,
};

const { NONE, ...scopeWithDataCollected } = COLLECTED_DATA_SCOPE;

export const isEditBenefitDataButtonVisible = (
  forms: ISubscriptionForm[],
  activeForms: ISubscriptionActiveForm[],
  collectedDataScope: SubscriptionCollectedDataScope
): boolean => {
  const isBenefitCollectingData = Object.values(
    scopeWithDataCollected
  ).includes(collectedDataScope);

  const isButtonVisible =
    hasAnyForms(forms, activeForms) && isBenefitCollectingData;

  return isButtonVisible;
};

export const hasAnyForms = (
  forms: ISubscriptionForm[],
  activeForms: ISubscriptionActiveForm[]
): boolean => {
  if (!isArrayHasItems(forms) && !isArrayHasItems(activeForms)) return false;

  return true;
};

const calculatePrice = (
  purchaseMethod: SubscriptionPurchaseMethod,
  price: string | number,
  ratio: number,
  ifNotIndividualValue?: number
) => {
  const calculatedPrice =
    purchaseMethod === SubscriptionPurchaseMethod.INDIVIDUAL
      ? Number(price) * ratio
      : ifNotIndividualValue || price;

  return String(calculatedPrice);
};

type GetSplittedBenefits = (
  benefits: ISubscriptionItemCurrentUserSubscription[]
) => ISubscriptionItemCurrentUserSubscription[];

type NewBenefits = (
  | ISubscriptionItemCurrentUserSubscription[]
  | ISubscriptionItemCurrentUserSubscription
)[];

const getFormsByReceiver = <T extends { receiverId: string }>(
  forms: T[],
  receivers: ISubscriptionReceiver[],
  isPending = false
) => {
  return forms.filter(
    (form) =>
      receivers.find((receiver) => form.receiverId === receiver.id)?.pending ===
      isPending
  );
};

export const getSplittedBenefits: GetSplittedBenefits = (benefits) => {
  const newBenefits: NewBenefits = [...benefits];

  benefits.forEach((benefit, index) => {
    if (benefit.status === SubscriptionStatus.NEW_PERSON) {
      const { activeForms, forms, receivers } = benefit || {};
      const benefitReceiversNumber = receivers?.length;

      const activeReceiversNumber = receivers?.reduce((acc, cur) => {
        return cur.status !== SubscriptionStatus.ACTIVE ? acc : acc + 1;
      }, 0);
      const activeReceiversRatio =
        activeReceiversNumber / benefitReceiversNumber;

      const pendingReceiversNumber =
        benefitReceiversNumber - activeReceiversNumber;
      const pendingReceiversRatio =
        pendingReceiversNumber / benefitReceiversNumber;

      const activePart: ISubscriptionItemCurrentUserSubscription = {
        ...benefit,
        activeForms: getFormsByReceiver<ISubscriptionActiveForm>(
          activeForms,
          receivers
        ),
        forms: getFormsByReceiver<ISubscriptionForm>(forms, receivers),
        status: SubscriptionStatus.ACTIVE,
        quantity: benefit.quantity * activeReceiversRatio,
        employeePrice: calculatePrice(
          benefit.purchaseMethod,
          benefit.employeePrice,
          activeReceiversRatio
        ),
        employerPrice: calculatePrice(
          benefit.purchaseMethod,
          benefit.employerPrice,
          activeReceiversRatio
        ),
      };
      const pendingPart: ISubscriptionItemCurrentUserSubscription = {
        ...benefit,
        activeForms: getFormsByReceiver<ISubscriptionActiveForm>(
          activeForms,
          receivers,
          true
        ),
        forms: getFormsByReceiver<ISubscriptionForm>(forms, receivers, true),
        status: SubscriptionStatus.PENDING,
        quantity: benefit.quantity * pendingReceiversRatio,
        employeePrice: calculatePrice(
          benefit.purchaseMethod,
          benefit.employeePrice,
          pendingReceiversRatio,
          0
        ),
        employerPrice: calculatePrice(
          benefit.purchaseMethod,
          benefit.employerPrice,
          pendingReceiversRatio,
          0
        ),
      };
      newBenefits[index] = [activePart, pendingPart];
    }
  });

  return newBenefits.flat();
};
